import formatterPrice from '@/utils/number/formatterPrice';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';
import { WORKORDER_ITEMS_QUERY_KEY } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { useWorkorderComplementSyncWithPecaAi } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import {
  useWorkOrderPecaAiRemoveProductInBasket,
  WORKORDER_PECA_AI_BASKET_DETAIL,
} from '@/core/domain/workorder/pecaAi/workorder-pecaAi.hooks';
import {
  Avatar,
  Button,
  Empty,
  List,
  Modal,
  Row,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { Package, ShoppingCart, Trash2 } from 'react-feather';
import { PecaAiProduct, usePecaAiContext } from '../../PecaAiProvider';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { Group } from '@/components/_common';

import './styles.less';

interface Props {
  hasBasket: boolean;
}

export default function PecaAiAdvancedBasket({ hasBasket }: Props) {
  const {
    products,
    removeProduct: removeNewProduct,
    basketId,
    cnpj,
  } = usePecaAiContext();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const removeProduct = useWorkOrderPecaAiRemoveProductInBasket();
  const syncWithPecaAi = useWorkorderComplementSyncWithPecaAi();
  const queryChanel = useQueryChanel();

  const productsToShow = products.filter((product) =>
    hasBasket ? product.inBasket : !product.inBasket,
  );

  function getProductTotal(product: PecaAiProduct) {
    return Number(
      product.totalPrice || product.unitPrice * product.amount || 0,
    );
  }

  const totalAmount = productsToShow.reduce(
    (acc, product) => acc + Number(product.amount || 0),
    0,
  );
  const totalPrice = productsToShow.reduce(
    (acc, product) => acc + getProductTotal(product),
    0,
  );

  const isLoading = removeProduct.isLoading || syncWithPecaAi.isLoading;

  const handleRemove = (product: PecaAiProduct) => {
    Modal.confirm({
      title: 'Remover produto da cesta',
      content: `Deseja remover o produto "${product.description}" da cesta?`,
      okText: 'Remover',
      okType: 'danger',
      onOk: async () => {
        if (product.inBasket && basketId && cnpj && complement && workorder) {
          await removeProduct.mutateAsync({
            document: cnpj,
            basketId,
            productId: product.productId,
          });

          await syncWithPecaAi.mutateAsync({
            complementId: complement.id,
            workOrderId: workorder.id,
          });

          queryChanel({
            invalidateQueries: [
              [WORKORDER_PECA_AI_BASKET_DETAIL],
              [WORKORDER_ITEMS_QUERY_KEY],
            ],
          });

          return;
        }

        if (product.basketItemId) {
          removeNewProduct(product.basketItemId);
        }
      },
    });
  };

  return (
    <aside className="peca-ai-advanced-basket">
      <Row
        align="middle"
        justify="space-between"
        className="peca-ai-advanced-basket__header"
      >
        <Group gap={5}>
          <ShoppingCart size={18} />
          <Typography.Text strong>Cesta</Typography.Text>
        </Group>

        <Tag color={productsToShow.length ? 'blue' : 'default'}>
          {totalAmount}
        </Tag>
      </Row>

      {!productsToShow.length && (
        <Empty
          className="peca-ai-advanced-basket__empty"
          description="Nenhum produto na cesta"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {!!productsToShow.length && (
        <List
          className="peca-ai-advanced-basket__items"
          dataSource={productsToShow}
          rowKey={(product) => product.basketItemId || product.productId}
          renderItem={(product) => (
            <List.Item className="peca-ai-advanced-basket__item">
              <Avatar
                shape="square"
                size={44}
                src={product.image}
                icon={<Package size={17} />}
                className="peca-ai-advanced-basket__thumb"
              />

              <div className="peca-ai-advanced-basket__item-info">
                <Typography.Text
                  strong
                  ellipsis={{ tooltip: product.description }}
                >
                  {product.description}
                </Typography.Text>

                <Typography.Text type="secondary">
                  {product.amount} un.{' '}
                  {product.brand ? `- ${product.brand}` : ''}
                </Typography.Text>

                <Typography.Text strong>
                  {formatterPrice(getProductTotal(product))}
                </Typography.Text>
              </div>

              <Tooltip title="Remover">
                <Button
                  type="text"
                  danger
                  disabled={isLoading}
                  icon={<Trash2 size={15} />}
                  onClick={() => handleRemove(product)}
                  className="peca-ai-advanced-basket__remove"
                />
              </Tooltip>
            </List.Item>
          )}
        />
      )}

      <Row
        align="middle"
        justify="space-between"
        className="peca-ai-advanced-basket__footer"
      >
        <Typography.Text type="secondary">Subtotal</Typography.Text>
        <Typography.Text strong>{formatterPrice(totalPrice)}</Typography.Text>
      </Row>
    </aside>
  );
}
